import React, { useState, useCallback } from "react";

/**
 * Slurp-styled replacement for window.confirm / window.alert. Hook
 * returns three things:
 *   - confirm(message, opts)  → Promise<boolean>
 *   - alert(message, opts)    → Promise<void>   (single OK button)
 *   - dialog                  → JSX to render at the end of your tree
 *
 * Both calls return a promise so flow code reads like the native ones.
 * `opts` accepts { title, confirmLabel, cancelLabel, danger }.
 */
export function useConfirmDialog() {
  const [state, setState] = useState(null);

  const confirm = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setState({ message, alertOnly: false, ...opts, resolve });
    });
  }, []);

  const alert = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setState({ message, alertOnly: true, ...opts, resolve });
    });
  }, []);

  const close = useCallback((value) => {
    setState((s) => {
      if (s?.resolve) s.resolve(value);
      return null;
    });
  }, []);

  const dialog = state ? (
    <ConfirmDialog
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel || (state.alertOnly ? "OK" : "Confirm")}
      cancelLabel={state.alertOnly ? null : (state.cancelLabel || "Cancel")}
      danger={!!state.danger}
      onConfirm={() => close(true)}
      onClose={() => close(false)}
    />
  ) : null;

  return { confirm, alert, dialog };
}

function ConfirmDialog({ title, message, confirmLabel, cancelLabel, danger, onConfirm, onClose }) {
  return (
    <div className="pm-modal__bg" onClick={onClose}>
      <div className="pm-modal pm-confirm-modal" onClick={(e) => e.stopPropagation()}>
        {title && <h2 className="pm-modal__title">{title}</h2>}
        <p className="pm-confirm-modal__message">{message}</p>
        <div className="pm-confirm-modal__actions">
          {cancelLabel && (
            <button
              type="button"
              className="pm-group__sticker pm-group__sticker--cream"
              onClick={onClose}
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            className={`pm-group__sticker ${danger ? "pm-group__sticker--leave" : "pm-group__sticker--accent"}`}
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
